/**
 * Stale-build detector for WKWebView / PWA environments.
 *
 * How it works:
 *   1. At build time Vite injects __APP_BUILD_VERSION__ (a timestamp)
 *      and writes the same value to /version.json in the output.
 *   2. When the user foregrounds the app (visibilitychange → visible),
 *      we fetch /version.json with a cache-busting query string.
 *   3. If the server version differs from the baked-in version the
 *      JS bundle is stale → force a hard reload.
 *
 * Guards:
 *   - Skipped in dev mode (import.meta.env.DEV)
 *   - Throttled to once per 60 seconds so rapid bg/fg cycles don't spam
 *   - Fetch failures (offline / server error) are silently ignored
 */

const THROTTLE_MS = 60_000 // 1 minute

let lastCheckAt = 0

async function checkVersion() {
  // Skip in development
  if (import.meta.env.DEV) return

  // Throttle: don't check more than once per minute
  const now = Date.now()
  if (now - lastCheckAt < THROTTLE_MS) return
  lastCheckAt = now

  try {
    const res = await fetch(`/version.json?_t=${now}`, { cache: 'no-store' })
    if (!res.ok) return

    const data = await res.json()
    const serverVersion = data?.version

    // eslint-disable-next-line no-undef
    if (serverVersion && serverVersion !== __APP_BUILD_VERSION__) {
      if (import.meta.env.DEV) {
        console.info('[versionCheck] New build detected, reloading…')
      }
      window.location.reload()
    }
  } catch {
    // Offline or network error — ignore silently
  }
}

/**
 * Call once at app startup (e.g. in main.jsx).
 * Sets up a visibilitychange listener that checks for new builds
 * every time the user returns to the app.
 */
export function initVersionCheck() {
  if (import.meta.env.DEV) return

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkVersion()
    }
  })

  // Also check on pageshow (back-forward cache in Safari / WKWebView)
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      checkVersion()
    }
  })
}
